/**
 * Pure decision logic for MassageTypeSelect's keydown handling.
 *
 * The intermittent "failed to add a treatment" bug was caused by
 * MassageTypeSelect always forwarding every keydown to the caller's
 * onKeyDown, even when it had already consumed the key itself (selecting a
 * highlighted dropdown option on Enter). The caller's Enter-to-submit
 * handler would then fire in the same keystroke, submitting a stale
 * massageType/price from before the selection's state updates had applied.
 *
 * This module decides, given the current dropdown state and the pressed
 * key, what MassageTypeSelect should do internally and whether the key
 * should still be forwarded to the caller - without touching the DOM, React
 * state, or a component, so the contract can be unit tested directly.
 */

export type MassageTypeSelectKeyAction =
    | { kind: 'open-and-highlight-first' }
    | { kind: 'navigate'; direction: 'up' | 'down' }
    | { kind: 'select'; index: number }
    | { kind: 'close-dropdown' }
    | { kind: 'none' };

export interface MassageTypeSelectKeyInput {
    key: string;
    isOpen: boolean;
    highlightIndex: number;
    optionCount: number;
}

export interface MassageTypeSelectKeyDecision {
    action: MassageTypeSelectKeyAction;
    preventDefault: boolean;
    /**
     * Whether the caller's onKeyDown should still be invoked for this key.
     * False for navigation and for Enter-selecting-a-highlighted-option
     * (MassageTypeSelect fully owns those). True for everything else,
     * including free-text Enter (no highlighted option to select - either
     * the dropdown is closed, or the typed text - including a brand-new
     * service name - does not match anything) so the caller's submit
     * handler still runs.
     */
    forward: boolean;
}

export function decideMassageTypeSelectKey(input: MassageTypeSelectKeyInput): MassageTypeSelectKeyDecision {
    const { key, isOpen, highlightIndex, optionCount } = input;

    if (key === 'ArrowDown') {
        return {
            action: isOpen ? { kind: 'navigate', direction: 'down' } : { kind: 'open-and-highlight-first' },
            preventDefault: true,
            forward: false,
        };
    }

    if (key === 'ArrowUp') {
        return { action: { kind: 'navigate', direction: 'up' }, preventDefault: true, forward: false };
    }

    if (key === 'Enter' && isOpen && highlightIndex >= 0 && highlightIndex < optionCount) {
        return { action: { kind: 'select', index: highlightIndex }, preventDefault: true, forward: false };
    }

    if (key === 'Escape') {
        return { action: { kind: 'close-dropdown' }, preventDefault: false, forward: true };
    }

    // Free-text Enter (including a brand-new service name with no matching
    // option) and every other key: no internal action, always forwarded.
    return { action: { kind: 'none' }, preventDefault: false, forward: true };
}
